import { useCallback, useEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { Input, type InputProps } from '../Input/index.js';
import { IcnSearch } from '../../icons/index.js';

export interface SearchInputProps extends Omit<InputProps, 'prefix' | 'type' | 'onValueChange'> {
  /** Controlled search value. */
  value?: string;
  /** Uncontrolled default value. */
  defaultValue?: string;
  /** Fires for every keystroke after the debounce window elapses. Immediate
   *  controlled updates still propagate through `onValueChange`. */
  onDebouncedChange?: (value: string) => void;
  /** Fires immediately on every keystroke (no debounce). */
  onValueChange?: (value: string) => void;
  /** Debounce window for `onDebouncedChange`, ms. Default 200. Set 0 to
   *  disable debouncing entirely (fires in the same tick). */
  debounce?: number;
  ref?: Ref<HTMLInputElement>;
}

/** Thin wrapper around Input with a 🔍 prefix, role=searchbox semantics,
 *  and a built-in debouncer that sends idle-trailing events to
 *  `onDebouncedChange`. Spec 15 §10. */
export function SearchInput(props: SearchInputProps): ReactNode {
  const {
    value: controlled,
    defaultValue,
    onDebouncedChange,
    onValueChange,
    debounce = 200,
    placeholder,
    ...rest
  } = props;

  const isControlled = controlled !== undefined;
  const [internal, setInternal] = useState<string>(defaultValue ?? '');
  const value = isControlled ? controlled : internal;
  const timerRef = useRef<number | null>(null);

  const fireDebounced = useCallback(
    (next: string): void => {
      if (onDebouncedChange === undefined) return;
      if (debounce <= 0) {
        onDebouncedChange(next);
        return;
      }
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        onDebouncedChange(next);
      }, debounce);
    },
    [debounce, onDebouncedChange],
  );

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const handle = useCallback(
    (next: string): void => {
      if (!isControlled) setInternal(next);
      onValueChange?.(next);
      fireDebounced(next);
    },
    [fireDebounced, isControlled, onValueChange],
  );

  return (
    <Input
      {...rest}
      type="search"
      role="searchbox"
      placeholder={placeholder}
      prefix={<IcnSearch size={14} />}
      value={value}
      clearable
      onValueChange={handle}
      data-component="SearchInput"
    />
  );
}
